import * as React from "react";

import { useBodyScrollLock } from "./useBodyScrollLock";

/**
 * Accessibility plumbing for a modal overlay that is portalled OUTSIDE the app root (e.g. a panel
 * hosting a cross-origin iframe, where Radix's FocusScope can't be used because it would steal
 * focus out of the iframe). While `active`:
 *  - locks body scroll with `position: fixed` (iOS-Safari-safe, unlike `overflow: hidden`) and
 *    restores the scroll position on release;
 *  - marks the app root `inert` + `aria-hidden` so the rest of the page is removed from assistive
 *    tech and the tab order.
 *
 * Pair this with passive focus sentinels in the overlay itself for a complete iframe-safe modal.
 */
export function useModalA11y(active: boolean, rootSelector = "#root"): void {
  useBodyScrollLock(active);

  // Hide the rest of the app (under rootSelector, outside the portalled overlay) from AT + keyboard.
  React.useEffect(() => {
    if (!active) return;
    const root = document.querySelector(rootSelector);
    if (!root) return;
    const previousAriaHidden = root.getAttribute("aria-hidden");
    const hadInert = root.hasAttribute("inert");
    root.setAttribute("aria-hidden", "true");
    root.setAttribute("inert", "");
    return () => {
      if (previousAriaHidden === null) root.removeAttribute("aria-hidden");
      else root.setAttribute("aria-hidden", previousAriaHidden);
      if (hadInert) root.setAttribute("inert", "");
      else root.removeAttribute("inert");
    };
  }, [active, rootSelector]);
}
